import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { 
  FaCalendarAlt, FaEnvelope, FaWhatsapp, FaBell, FaCog, FaCheckCircle, 
  FaHistory, FaPaperPlane, FaPlus, FaSave, FaExclamationTriangle, FaShieldAlt, FaRocket, FaTrashAlt 
} from 'react-icons/fa';

interface SocietyYear {
  id: number;
  year_label: string;
  start_date?: string;
  end_date?: string;
  status: 'ACTIVE' | 'ARCHIVED' | 'UPCOMING';
  created_at?: string;
}

const SystemSettingsAdmin: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'SOCIETY_YEARS' | 'EMAIL' | 'WHATSAPP' | 'BROADCAST' | 'RESET_DATA'>('SOCIETY_YEARS');
  const [resettingData, setResettingData] = useState(false);

  // 1. Society Years State
  const [societyYears, setSocietyYears] = useState<SocietyYear[]>([]);
  const [activeSocietyYear, setActiveSocietyYear] = useState<SocietyYear | null>(null);
  const [newYearForm, setNewYearForm] = useState({ year_label: '', start_date: '', end_date: '' });
  const [loadingYears, setLoadingYears] = useState(false);
  const [activatingId, setActivatingId] = useState<number | null>(null);

  // 2. Notification Settings State (Email SMTP + WhatsApp Meta API)
  const [notifSettings, setNotifSettings] = useState({
    smtp_host: 'smtp.gmail.com',
    smtp_port: 587,
    smtp_user: '',
    smtp_pass: '',
    sender_email: 'notifications@rmbjaipur.com',
    sender_name: 'RMB Jaipur Fellowship',
    email_notifications_enabled: true,
    email_welcome: true,
    email_activity_approved: true,
    email_events: true,
    email_birthday: true,
    meta_phone_number_id: '',
    meta_waba_id: '',
    meta_access_token: '',
    meta_app_secret: '',
    meta_verify_token: '',
    whatsapp_notifications_enabled: true,
    wa_welcome: true,
    wa_activity_approved: true,
    wa_meeting_reminder: true,
    wa_birthday: true
  });
  const [savingSettings, setSavingSettings] = useState(false);

  // Members list & target selection for reset
  const [membersList, setMembersList] = useState<any[]>([]);
  const [selectedMemberId, setSelectedMemberId] = useState<string>('ALL');

  // Test triggers state
  const [testEmail, setTestEmail] = useState('');
  const [testPhone, setTestPhone] = useState('');
  const [testStatus, setTestStatus] = useState<string | null>(null);

  // 3. Broadcast Notification State
  const [broadcastForm, setBroadcastForm] = useState({ title: '', message: '', type: 'SYSTEM' });
  const [sendingBroadcast, setSendingBroadcast] = useState(false);

  useEffect(() => {
    fetchSocietyYears();
    fetchNotificationSettings();
    axios.get('https://rmbbackend.rmbjaipur.com/auth/allusersinfo')
      .then(res => {
        const list = Array.isArray(res.data) ? res.data : [];
        setMembersList(list.filter((u: any) => u.role !== 'super-admin' && u.delete !== 1 && u.name));
      })
      .catch(err => console.error("Error fetching members in SystemSettingsAdmin:", err));
  }, []);

  const handleResetAllDashboardData = async () => {
    const isAll = selectedMemberId === 'ALL';
    const targetText = isAll ? 'ALL MEMBERS (Whole Chapter)' : `Selected Member (ID: ${selectedMemberId})`;
    const confirmMsg = `⚠️ CONFIRM DATA RESET TO ZERO\n\nTarget Scope: ${targetText}\n\nThis action will:\n1. Reset member points to ZERO.\n2. Reset Business Given and Business Received amounts to 0.00.\n3. Reset activity counts (R2Rs, Referrals, Visitors) to 0.\n\nNOTE: Member login credentials and profiles remain 100% active and will NOT be deleted.\n\nClick OK to confirm.`;

    if (!window.confirm(confirmMsg)) return;

    setResettingData(true);
    try {
      const res = await axios.post('https://rmbbackend.rmbjaipur.com/auth/admin/reset-all-dashboard-data', { member_id: selectedMemberId });
      alert(res.data.message || 'Dashboard data reset to ZERO successfully!');
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to reset dashboard data');
    } finally {
      setResettingData(false);
    }
  };

  const fetchSocietyYears = async () => {
    setLoadingYears(true);
    try {
      const res = await axios.get('https://rmbbackend.rmbjaipur.com/auth/society-years');
      setSocietyYears(res.data.data || []);
      setActiveSocietyYear(res.data.activeYear || null);
    } catch (err) {
      console.error('Error fetching society years:', err);
    } finally {
      setLoadingYears(false);
    }
  };

  const fetchNotificationSettings = async () => {
    try {
      const res = await axios.get('https://rmbbackend.rmbjaipur.com/auth/notification-settings');
      if (res.data.data) {
        setNotifSettings(prev => ({
          ...prev,
          ...res.data.data,
          email_notifications_enabled: Boolean(res.data.data.email_notifications_enabled),
          email_welcome: Boolean(res.data.data.email_welcome),
          email_activity_approved: Boolean(res.data.data.email_activity_approved),
          email_events: Boolean(res.data.data.email_events),
          email_birthday: Boolean(res.data.data.email_birthday),
          whatsapp_notifications_enabled: Boolean(res.data.data.whatsapp_notifications_enabled),
          wa_welcome: Boolean(res.data.data.wa_welcome),
          wa_activity_approved: Boolean(res.data.data.wa_activity_approved),
          wa_meeting_reminder: Boolean(res.data.data.wa_meeting_reminder),
          wa_birthday: Boolean(res.data.data.wa_birthday)
        }));
      }
    } catch (err) {
      console.error('Error fetching notification settings:', err);
    }
  };

  const handleCreateSocietyYear = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newYearForm.year_label.trim()) return alert('Please enter society year label (e.g. 2026-2027)');

    try {
      const res = await axios.post('https://rmbbackend.rmbjaipur.com/auth/society-years', newYearForm);
      alert(res.data.message);
      setNewYearForm({ year_label: '', start_date: '', end_date: '' });
      fetchSocietyYears();
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to create society year');
    }
  };

  const handleActivateSocietyYear = async (sy: SocietyYear) => {
    const confirmMsg = `⚠️ ARE YOU SURE YOU WANT TO ACTIVATE SOCIETY YEAR ${sy.year_label}?\n\nAUTOMATED ACTIONS THAT WILL OCCUR:\n1. All current activity data will be archived to Last Years history.\n2. All active member leaderboard points will be reset to 0.\n3. Society Year ${sy.year_label} will become the active chapter tenure.\n4. Notification broadcast will be sent to all member portals.`;
    
    if (!window.confirm(confirmMsg)) return;

    setActivatingId(sy.id);
    try {
      const res = await axios.post(`https://rmbbackend.rmbjaipur.com/auth/society-years/activate/${sy.id}`);
      alert(res.data.message);
      fetchSocietyYears();
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to activate society year');
    } finally {
      setActivatingId(null);
    }
  };

  const handleSaveNotificationSettings = async (e: React.FormEvent) => {
    e.preventDefault();
    setSavingSettings(true);
    try {
      const res = await axios.post('https://rmbbackend.rmbjaipur.com/auth/notification-settings', notifSettings);
      alert(res.data.message);
      fetchNotificationSettings();
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to save settings');
    } finally {
      setSavingSettings(false);
    }
  };

  const handleSendTestEmail = async () => {
    if (!testEmail) return alert('Please enter target test email address');
    try {
      const res = await axios.post('https://rmbbackend.rmbjaipur.com/auth/test-email', { recipient_email: testEmail });
      setTestStatus(res.data.message);
    } catch (err: any) {
      alert('Failed to send test email');
    }
  };

  const handleSendTestWhatsApp = async () => {
    if (!testPhone) return alert('Please enter target test WhatsApp phone number');
    try {
      const res = await axios.post('https://rmbbackend.rmbjaipur.com/auth/test-whatsapp', { recipient_phone: testPhone });
      setTestStatus(res.data.message);
    } catch (err: any) {
      alert('Failed to send test WhatsApp message');
    }
  };

  const handleSendBroadcast = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!broadcastForm.title || !broadcastForm.message) return alert('Please enter title and message');

    setSendingBroadcast(true);
    try {
      const res = await axios.post('https://rmbbackend.rmbjaipur.com/auth/admin/broadcast-notification', broadcastForm);
      alert(res.data.message);
      setBroadcastForm({ title: '', message: '', type: 'SYSTEM' });
    } catch (err: any) {
      alert('Failed to publish broadcast notification');
    } finally {
      setSendingBroadcast(false);
    }
  };

  return (
    <div className="p-6 bg-slate-900 min-h-screen text-slate-100 space-y-6">
      {/* Header Bar */}
      <div className="bg-[#1E2B45] p-6 rounded-3xl border border-blue-500/40 shadow-2xl flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h1 className="text-2xl font-black text-white flex items-center gap-3">
            <FaCog className="text-[#FDBC12] animate-spin-slow" /> System Settings & Society Years Admin
          </h1>
          <p className="text-xs text-slate-400 mt-1">
            Manage Rotary Society Years, Automated Tenure Rollovers, Email SMTP Gateway, WhatsApp Meta Business API & Member Notifications.
          </p>
        </div>

        {/* Tab Selection Pill */}
        <div className="flex flex-wrap bg-[#131E32] p-1.5 rounded-2xl border border-[#2A3B5C]">
          <button
            onClick={() => setActiveTab('SOCIETY_YEARS')}
            className={`px-4 py-2 rounded-xl text-xs font-bold transition flex items-center gap-2 cursor-pointer ${
              activeTab === 'SOCIETY_YEARS' ? 'bg-[#FDBC12] text-[#0A2451] font-black shadow-lg' : 'text-slate-300 hover:text-white'
            }`}
          >
            <FaCalendarAlt /> Society Years & Rollover
          </button>
          <button
            onClick={() => setActiveTab('EMAIL')}
            className={`px-4 py-2 rounded-xl text-xs font-bold transition flex items-center gap-2 cursor-pointer ${
              activeTab === 'EMAIL' ? 'bg-[#FDBC12] text-[#0A2451] font-black shadow-lg' : 'text-slate-300 hover:text-white'
            }`}
          >
            <FaEnvelope /> Email SMTP Config
          </button>
          <button
            onClick={() => setActiveTab('WHATSAPP')}
            className={`px-4 py-2 rounded-xl text-xs font-bold transition flex items-center gap-2 cursor-pointer ${
              activeTab === 'WHATSAPP' ? 'bg-[#FDBC12] text-[#0A2451] font-black shadow-lg' : 'text-slate-300 hover:text-white'
            }`}
          >
            <FaWhatsapp /> WhatsApp Meta API
          </button>
          <button
            onClick={() => setActiveTab('BROADCAST')}
            className={`px-4 py-2 rounded-xl text-xs font-bold transition flex items-center gap-2 cursor-pointer ${
              activeTab === 'BROADCAST' ? 'bg-[#FDBC12] text-[#0A2451] font-black shadow-lg' : 'text-slate-300 hover:text-white'
            }`}
          >
            <FaBell /> Member Broadcasts
          </button>
          <button
            onClick={() => setActiveTab('RESET_DATA')}
            className={`px-4 py-2 rounded-xl text-xs font-bold transition flex items-center gap-2 cursor-pointer ${
              activeTab === 'RESET_DATA' ? 'bg-rose-600 text-white font-black shadow-lg' : 'text-rose-400 hover:text-rose-300'
            }`}
          >
            <FaTrashAlt /> 🧹 Reset Dashboard Data
          </button>
        </div>
      </div>

      {/* TAB 1: SOCIETY YEARS & AUTOMATED TENURE ROLLOVER */}
      {activeTab === 'SOCIETY_YEARS' && (
        <div className="space-y-6">
          {/* Active Society Year Banner */}
          {activeSocietyYear && (
            <div className="bg-gradient-to-r from-amber-500/20 via-amber-600/10 to-transparent p-5 rounded-2xl border border-amber-500/40 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-2xl bg-[#FDBC12] text-[#0A2451] flex items-center justify-center font-black text-xl shadow-lg">
                  🏆
                </div>
                <div>
                  <span className="text-[10px] font-black text-[#FDBC12] uppercase tracking-widest block">CURRENT ACTIVE TENURE</span>
                  <h2 className="text-xl font-black text-white">Society Tenure Year: {activeSocietyYear.year_label}</h2>
                  <p className="text-xs text-slate-300">
                    Duration: {activeSocietyYear.start_date || 'N/A'} to {activeSocietyYear.end_date || 'N/A'}
                  </p>
                </div>
              </div>
              <span className="px-4 py-1.5 bg-emerald-500/20 text-emerald-400 border border-emerald-500/40 rounded-full font-black text-xs uppercase tracking-wider flex items-center gap-1.5">
                <FaCheckCircle /> Live & Active
              </span>
            </div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Left: Create New Society Year Form */}
            <div className="bg-[#1E2B45] p-6 rounded-3xl border border-[#2A3B5C] shadow-xl space-y-4">
              <h2 className="text-base font-black text-white flex items-center gap-2 border-b border-[#2A3B5C] pb-3">
                <FaPlus className="text-[#FDBC12]" /> Create New Society Year
              </h2>

              <form onSubmit={handleCreateSocietyYear} className="space-y-4 text-xs">
                <div>
                  <label className="font-bold text-slate-300 block mb-1">Tenure Year Label *</label>
                  <input
                    type="text"
                    value={newYearForm.year_label}
                    onChange={(e) => setNewYearForm({ ...newYearForm, year_label: e.target.value })}
                    placeholder="e.g. 2026-2027"
                    className="w-full p-3 bg-[#131E32] border border-[#2A3B5C] rounded-xl text-white font-bold outline-none"
                    required
                  />
                  <p className="text-[10px] text-slate-400 mt-1">Standard format: YYYY-YYYY (e.g., 2026-2027)</p>
                </div>

                <div>
                  <label className="font-bold text-slate-300 block mb-1">Start Date</label>
                  <input
                    type="date"
                    value={newYearForm.start_date}
                    onChange={(e) => setNewYearForm({ ...newYearForm, start_date: e.target.value })}
                    className="w-full p-3 bg-[#131E32] border border-[#2A3B5C] rounded-xl text-white outline-none"
                  />
                </div>

                <div>
                  <label className="font-bold text-slate-300 block mb-1">End Date</label>
                  <input
                    type="date"
                    value={newYearForm.end_date}
                    onChange={(e) => setNewYearForm({ ...newYearForm, end_date: e.target.value })}
                    className="w-full p-3 bg-[#131E32] border border-[#2A3B5C] rounded-xl text-white outline-none"
                  />
                </div>

                <button
                  type="submit"
                  className="w-full py-3 bg-[#FDBC12] hover:bg-amber-400 text-[#0A2451] font-black rounded-xl shadow-lg flex items-center justify-center gap-2 cursor-pointer transition"
                >
                  <FaPlus /> Add Society Tenure Year
                </button>
              </form>
            </div>

            {/* Right: Existing Society Years & Rollover Actions */}
            <div className="lg:col-span-2 bg-[#1E2B45] p-6 rounded-3xl border border-[#2A3B5C] shadow-xl space-y-4">
              <div className="flex justify-between items-center border-b border-[#2A3B5C] pb-3">
                <h2 className="text-base font-black text-white flex items-center gap-2">
                  <FaHistory className="text-[#FDBC12]" /> Chapter Society Tenure Years History
                </h2>
                <span className="text-xs text-slate-400 font-bold">Total: {societyYears.length}</span>
              </div>

              <div className="space-y-3">
                {societyYears.map((sy) => (
                  <div 
                    key={sy.id} 
                    className={`p-4 rounded-2xl border transition flex flex-col sm:flex-row sm:items-center justify-between gap-4 ${
                      sy.status === 'ACTIVE'
                        ? 'bg-[#131E32] border-amber-500/50 shadow-md'
                        : 'bg-[#131E32]/60 border-[#2A3B5C]'
                    }`}
                  >
                    <div>
                      <div className="flex items-center gap-2">
                        <h3 className="font-black text-white text-base">{sy.year_label}</h3>
                        <span className={`px-2.5 py-0.5 rounded-full text-[10px] font-black uppercase ${
                          sy.status === 'ACTIVE'
                            ? 'bg-amber-500/20 text-amber-300 border border-amber-500/40'
                            : sy.status === 'ARCHIVED'
                            ? 'bg-slate-700 text-slate-300'
                            : 'bg-blue-500/20 text-blue-300 border border-blue-500/40'
                        }`}>
                          {sy.status}
                        </span>
                      </div>
                      <p className="text-xs text-slate-400 mt-1">
                        Tenure Period: {sy.start_date || 'July 1'} to {sy.end_date || 'June 30'}
                      </p>
                    </div>

                    {sy.status !== 'ACTIVE' && (
                      <button
                        onClick={() => handleActivateSocietyYear(sy)}
                        disabled={activatingId === sy.id}
                        className="px-4 py-2 bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white font-black text-xs rounded-xl shadow-lg flex items-center gap-2 cursor-pointer transition self-start sm:self-auto"
                      >
                        <FaRocket /> {activatingId === sy.id ? 'Processing Rollover...' : 'Activate & Auto-Rollover Points'}
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* TAB 2: EMAIL NOTIFICATION CONFIGURATION (SMTP) */}
      {activeTab === 'EMAIL' && (
        <div className="bg-[#1E2B45] p-6 rounded-3xl border border-[#2A3B5C] shadow-2xl space-y-6 max-w-3xl mx-auto">
          <div className="border-b border-[#2A3B5C] pb-4 flex justify-between items-center">
            <div>
              <h2 className="text-base font-black text-amber-300 flex items-center gap-2 uppercase tracking-wider">
                <FaEnvelope /> Email Notification Gateway (SMTP Configuration)
              </h2>
              <p className="text-xs text-slate-400 mt-1">
                Configure outgoing SMTP email server for sending activity approval notices, welcome emails, and meeting alerts.
              </p>
            </div>
          </div>

          <form onSubmit={handleSaveNotificationSettings} className="space-y-6 text-xs">
            {/* Enable/Disable Toggle */}
            <div className="flex items-center justify-between bg-[#131E32] p-4 rounded-2xl border border-[#2A3B5C]">
              <div>
                <span className="font-bold text-white text-sm block">Global Email Notifications</span>
                <span className="text-slate-400 text-xs">Enable sending outgoing emails from RMB system</span>
              </div>
              <input
                type="checkbox"
                checked={notifSettings.email_notifications_enabled}
                onChange={(e) => setNotifSettings({ ...notifSettings, email_notifications_enabled: e.target.checked })}
                className="w-5 h-5 accent-[#FDBC12] cursor-pointer"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="font-bold text-slate-300 block mb-1">SMTP Host Server *</label>
                <input
                  type="text"
                  value={notifSettings.smtp_host}
                  onChange={(e) => setNotifSettings({ ...notifSettings, smtp_host: e.target.value })}
                  placeholder="e.g. smtp.gmail.com or mail.rmbjaipur.com"
                  className="w-full p-3 bg-[#131E32] border border-[#2A3B5C] rounded-xl text-white font-mono outline-none"
                  required
                />
              </div>

              <div>
                <label className="font-bold text-slate-300 block mb-1">SMTP Port *</label>
                <input
                  type="number"
                  value={notifSettings.smtp_port}
                  onChange={(e) => setNotifSettings({ ...notifSettings, smtp_port: Number(e.target.value) })}
                  placeholder="587"
                  className="w-full p-3 bg-[#131E32] border border-[#2A3B5C] rounded-xl text-white font-mono outline-none"
                  required
                />
              </div>

              <div>
                <label className="font-bold text-slate-300 block mb-1">SMTP Username / Email *</label>
                <input
                  type="text"
                  value={notifSettings.smtp_user}
                  onChange={(e) => setNotifSettings({ ...notifSettings, smtp_user: e.target.value })}
                  placeholder="notifications@rmbjaipur.com"
                  className="w-full p-3 bg-[#131E32] border border-[#2A3B5C] rounded-xl text-white outline-none"
                />
              </div>

              <div>
                <label className="font-bold text-slate-300 block mb-1">SMTP App Password *</label>
                <input
                  type="password"
                  value={notifSettings.smtp_pass}
                  onChange={(e) => setNotifSettings({ ...notifSettings, smtp_pass: e.target.value })}
                  placeholder="••••••••••••"
                  className="w-full p-3 bg-[#131E32] border border-[#2A3B5C] rounded-xl text-white outline-none"
                />
              </div>

              <div>
                <label className="font-bold text-slate-300 block mb-1">Sender Email Address</label>
                <input
                  type="email"
                  value={notifSettings.sender_email}
                  onChange={(e) => setNotifSettings({ ...notifSettings, sender_email: e.target.value })}
                  placeholder="info@rmbjaipur.com"
                  className="w-full p-3 bg-[#131E32] border border-[#2A3B5C] rounded-xl text-white outline-none"
                />
              </div>

              <div>
                <label className="font-bold text-slate-300 block mb-1">Sender Display Name</label>
                <input
                  type="text"
                  value={notifSettings.sender_name}
                  onChange={(e) => setNotifSettings({ ...notifSettings, sender_name: e.target.value })}
                  placeholder="RMB Jaipur Chapter"
                  className="w-full p-3 bg-[#131E32] border border-[#2A3B5C] rounded-xl text-white outline-none"
                />
              </div>
            </div>

            {/* Email Event Notification Toggles */}
            <div className="bg-[#131E32] p-4 rounded-2xl border border-[#2A3B5C] space-y-3">
              <span className="font-bold text-amber-300 text-xs block uppercase tracking-wider">Email Notification Trigger Rules</span>
              <div className="grid grid-cols-2 gap-3 text-slate-300">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={notifSettings.email_welcome} onChange={(e) => setNotifSettings({ ...notifSettings, email_welcome: e.target.checked })} className="accent-[#FDBC12]" />
                  <span>Welcome Email on Member Join</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={notifSettings.email_activity_approved} onChange={(e) => setNotifSettings({ ...notifSettings, email_activity_approved: e.target.checked })} className="accent-[#FDBC12]" />
                  <span>Activity Approval & Points Alert</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={notifSettings.email_events} onChange={(e) => setNotifSettings({ ...notifSettings, email_events: e.target.checked })} className="accent-[#FDBC12]" />
                  <span>Meeting & Event Invitations</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={notifSettings.email_birthday} onChange={(e) => setNotifSettings({ ...notifSettings, email_birthday: e.target.checked })} className="accent-[#FDBC12]" />
                  <span>Birthday & Anniversary Greetings</span>
                </label>
              </div>
            </div>

            {/* Test Email Button */}
            <div className="flex items-center gap-3 pt-2">
              <input
                type="email"
                value={testEmail}
                onChange={(e) => setTestEmail(e.target.value)}
                placeholder="Enter test email address..."
                className="flex-1 p-3 bg-[#131E32] border border-[#2A3B5C] rounded-xl text-white outline-none"
              />
              <button
                type="button"
                onClick={handleSendTestEmail}
                className="px-4 py-3 bg-blue-600 hover:bg-blue-500 text-white font-bold rounded-xl shadow cursor-pointer flex items-center gap-1.5"
              >
                <FaPaperPlane /> Test SMTP Email
              </button>
            </div>

            <button
              type="submit"
              disabled={savingSettings}
              className="px-6 py-3 bg-[#FDBC12] hover:bg-amber-400 text-[#0A2451] font-black rounded-xl shadow-lg flex items-center gap-2 cursor-pointer"
            >
              <FaSave /> {savingSettings ? 'Saving Settings...' : 'Save Email Configuration'}
            </button>
          </form>
        </div>
      )}

      {/* TAB 3: WHATSAPP META BUSINESS API CONFIGURATION */}
      {activeTab === 'WHATSAPP' && (
        <div className="bg-[#1E2B45] p-6 rounded-3xl border border-[#2A3B5C] shadow-2xl space-y-6 max-w-3xl mx-auto">
          <div className="border-b border-[#2A3B5C] pb-4">
            <h2 className="text-base font-black text-emerald-400 flex items-center gap-2 uppercase tracking-wider">
              <FaWhatsapp /> Meta WhatsApp Business Cloud API Settings
            </h2>
            <p className="text-xs text-slate-400 mt-1">
              Connect your official Meta WhatsApp Business Cloud API to send automated WhatsApp updates to RMB members.
            </p>
          </div>

          <form onSubmit={handleSaveNotificationSettings} className="space-y-6 text-xs">
            <div className="flex items-center justify-between bg-[#131E32] p-4 rounded-2xl border border-[#2A3B5C]">
              <div>
                <span className="font-bold text-white text-sm block">Global WhatsApp Notifications</span>
                <span className="text-slate-400 text-xs">Enable WhatsApp Business API messaging</span>
              </div>
              <input
                type="checkbox"
                checked={notifSettings.whatsapp_notifications_enabled}
                onChange={(e) => setNotifSettings({ ...notifSettings, whatsapp_notifications_enabled: e.target.checked })}
                className="w-5 h-5 accent-emerald-500 cursor-pointer"
              />
            </div>

            <div className="space-y-4">
              <div>
                <label className="font-bold text-slate-300 block mb-1">WhatsApp Phone Number ID *</label>
                <input
                  type="text"
                  value={notifSettings.meta_phone_number_id}
                  onChange={(e) => setNotifSettings({ ...notifSettings, meta_phone_number_id: e.target.value })}
                  placeholder="e.g. 109283746591023"
                  className="w-full p-3 bg-[#131E32] border border-[#2A3B5C] rounded-xl text-white font-mono outline-none"
                />
              </div>

              <div>
                <label className="font-bold text-slate-300 block mb-1">WhatsApp Business Account ID (WABA ID) *</label>
                <input
                  type="text"
                  value={notifSettings.meta_waba_id}
                  onChange={(e) => setNotifSettings({ ...notifSettings, meta_waba_id: e.target.value })}
                  placeholder="e.g. 987654321012345"
                  className="w-full p-3 bg-[#131E32] border border-[#2A3B5C] rounded-xl text-white font-mono outline-none"
                />
              </div>

              <div>
                <label className="font-bold text-slate-300 block mb-1">Meta Permanent System User Access Token *</label>
                <textarea
                  value={notifSettings.meta_access_token}
                  onChange={(e) => setNotifSettings({ ...notifSettings, meta_access_token: e.target.value })}
                  rows={3}
                  placeholder="EAAG..."
                  className="w-full p-3 bg-[#131E32] border border-[#2A3B5C] rounded-xl text-white font-mono text-xs outline-none"
                />
              </div>
            </div>

            {/* Test WhatsApp Trigger */}
            <div className="flex items-center gap-3 pt-2">
              <input
                type="text"
                value={testPhone}
                onChange={(e) => setTestPhone(e.target.value)}
                placeholder="Enter test phone number (e.g. 919876543210)..."
                className="flex-1 p-3 bg-[#131E32] border border-[#2A3B5C] rounded-xl text-white outline-none"
              />
              <button
                type="button"
                onClick={handleSendTestWhatsApp}
                className="px-4 py-3 bg-emerald-600 hover:bg-emerald-500 text-white font-bold rounded-xl shadow cursor-pointer flex items-center gap-1.5"
              >
                <FaPaperPlane /> Test WhatsApp Message
              </button>
            </div>

            <button
              type="submit"
              disabled={savingSettings}
              className="px-6 py-3 bg-[#FDBC12] hover:bg-amber-400 text-[#0A2451] font-black rounded-xl shadow-lg flex items-center gap-2 cursor-pointer"
            >
              <FaSave /> Save WhatsApp Meta API Configuration
            </button>
          </form>
        </div>
      )}

      {/* TAB 4: MEMBER BROADCAST CENTER */}
      {activeTab === 'BROADCAST' && (
        <div className="bg-[#1E2B45] p-6 rounded-3xl border border-[#2A3B5C] shadow-2xl space-y-6 max-w-2xl mx-auto">
          <div className="border-b border-[#2A3B5C] pb-4">
            <h2 className="text-base font-black text-amber-300 flex items-center gap-2 uppercase tracking-wider">
              <FaBell /> Broadcast Notification to Member Portals
            </h2>
            <p className="text-xs text-slate-400 mt-1">
              Publish an instant announcement alert that will appear in every member's portal notification bell drawer.
            </p>
          </div>

          <form onSubmit={handleSendBroadcast} className="space-y-4 text-xs">
            <div>
              <label className="font-bold text-slate-300 block mb-1">Notification Title *</label>
              <input
                type="text"
                value={broadcastForm.title}
                onChange={(e) => setBroadcastForm({ ...broadcastForm, title: e.target.value })}
                placeholder="e.g. Mandatory Member Meeting Next Tuesday"
                className="w-full p-3 bg-[#131E32] border border-[#2A3B5C] rounded-xl text-white font-bold outline-none"
                required
              />
            </div>

            <div>
              <label className="font-bold text-slate-300 block mb-1">Notification Message / Details *</label>
              <textarea
                value={broadcastForm.message}
                onChange={(e) => setBroadcastForm({ ...broadcastForm, message: e.target.value })}
                rows={4}
                placeholder="Write announcement message for members..."
                className="w-full p-3 bg-[#131E32] border border-[#2A3B5C] rounded-xl text-white outline-none"
                required
              />
            </div>

            <button
              type="submit"
              disabled={sendingBroadcast}
              className="w-full py-3 bg-gradient-to-r from-amber-400 to-amber-500 hover:from-amber-500 hover:to-amber-600 text-[#0A2451] font-black rounded-xl shadow-lg flex items-center justify-center gap-2 cursor-pointer transition"
            >
              <FaPaperPlane /> {sendingBroadcast ? 'Publishing...' : 'Publish Broadcast Notification'}
            </button>
          </form>
        </div>
      )}

      {/* TAB 5: RESET DASHBOARD & SYSTEM DATA TO ZERO */}
      {activeTab === 'RESET_DATA' && (
        <div className="bg-[#1E2B45] p-6 rounded-3xl border border-rose-500/40 shadow-2xl space-y-6 max-w-3xl mx-auto">
          <div className="border-b border-[#2A3B5C] pb-4 flex items-center gap-3">
            <div className="w-12 h-12 rounded-2xl bg-rose-500/20 text-rose-400 flex items-center justify-center font-black text-2xl border border-rose-500/40">
              🧹
            </div>
            <div>
              <h2 className="text-lg font-black text-rose-400 flex items-center gap-2 uppercase tracking-wider">
                Reset Dashboard Data & Activity Records to Zero
              </h2>
              <p className="text-xs text-slate-300 mt-1">
                Perform a clean reset of member points, business activity amounts, R2Rs, referrals, and visitor counts. Member login credentials and user profiles remain 100% active.
              </p>
            </div>
          </div>

          {/* Member Target Scope Selector */}
          <div className="bg-[#131E32] p-4 rounded-2xl border border-[#2A3B5C] space-y-2">
            <label className="block text-xs font-black text-amber-400 uppercase tracking-wider">
              👤 Select Target Scope for Data Reset:
            </label>
            <select
              value={selectedMemberId}
              onChange={(e) => setSelectedMemberId(e.target.value)}
              className="w-full p-3 bg-[#1E2B45] border border-[#2A3B5C] rounded-xl text-white font-bold text-xs outline-none cursor-pointer focus:border-amber-400 transition"
            >
              <option value="ALL">🌐 All Members (Whole Chapter Dashboard Reset)</option>
              {membersList.map((m: any) => (
                <option key={m.id} value={m.id}>
                  👤 {m.name} ({m.company_name || m.designation || 'Member'}) - ID: {m.id}
                </option>
              ))}
            </select>
            <p className="text-[11px] text-slate-400">
              🔒 Member login accounts, emails, passwords, events, and system settings are preserved and will NEVER be deleted.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Option 1: Reset Member Points Only */}
            <div className="bg-[#131E32] p-5 rounded-2xl border border-[#2A3B5C] flex flex-col justify-between space-y-4 shadow-lg">
              <div className="space-y-2">
                <div className="flex items-center gap-2 text-amber-400 font-bold text-sm">
                  <span>🎯</span> Option 1: Reset Points Only
                </div>
                <p className="text-xs text-slate-400">
                  Resets leaderboard points to 0 for {selectedMemberId === 'ALL' ? 'all active members' : 'the selected member'} while keeping historical activity descriptions.
                </p>
              </div>
              <button
                onClick={async () => {
                  const confirmMsg = `Reset points to 0 for ${selectedMemberId === 'ALL' ? 'ALL active members' : 'selected member'}?`;
                  if (!window.confirm(confirmMsg)) return;
                  try {
                    const res = await axios.post('https://rmbbackend.rmbjaipur.com/auth/members/reset-points', { target: selectedMemberId, newPoints: 0 });
                    alert(res.data.message);
                  } catch (err: any) {
                    alert('Failed to reset points');
                  }
                }}
                className="w-full py-2.5 bg-amber-500 hover:bg-amber-600 text-[#0A2451] font-black rounded-xl text-xs transition cursor-pointer shadow-md"
              >
                Reset {selectedMemberId === 'ALL' ? 'All' : 'Selected'} Member Points to 0
              </button>
            </div>

            {/* Option 2: Full System Reset - Make All Dashboard Data Zero */}
            <div className="bg-[#131E32] p-5 rounded-2xl border border-rose-500/40 flex flex-col justify-between space-y-4 shadow-lg">
              <div className="space-y-2">
                <div className="flex items-center gap-2 text-rose-400 font-bold text-sm">
                  <span>⚠️</span> Option 2: Reset Dashboard Data to 0
                </div>
                <p className="text-xs text-slate-400">
                  Resets business given/received amounts, R2R counts, referrals, visitors, and member points to ZERO for {selectedMemberId === 'ALL' ? 'all members' : 'the selected member'}.
                </p>
              </div>
              <button
                onClick={handleResetAllDashboardData}
                disabled={resettingData}
                className="w-full py-2.5 bg-rose-600 hover:bg-rose-700 text-white font-black rounded-xl text-xs transition shadow-lg flex items-center justify-center gap-2 cursor-pointer disabled:opacity-50"
              >
                <FaTrashAlt /> {resettingData ? 'Resetting Data...' : `Reset ${selectedMemberId === 'ALL' ? 'All Chapter' : 'Selected Member'} Data to ZERO`}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SystemSettingsAdmin;
